import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Registration, Survey } from '@spjp/model/registration';
import { RegistrationServiceBase } from '@common/services/registration.service.base';
import { API_URL, BUSINESS } from '@common/app/app.config';
import { CommunityService } from '@common/services/community.service';
import { Observable, combineLatest, from, throwError, of } from 'rxjs';
import { catchError, flatMap, map, switchMap, take, tap } from 'rxjs/operators';
import { AuthService } from '@common/services/auth.service';
import { CancelDelivery } from '@spjp/model/cancel-delivery.model';
import { Router } from '@angular/router';

@Injectable()
export class RegistrationService extends RegistrationServiceBase<Registration> {
    constructor(
        @Inject(BUSINESS) business,
        @Inject(API_URL) baseUrl: string,
        http: HttpClient,
        communityService: CommunityService,
        auth: AuthService,
        private router: Router,
    ) {
        super(business, http, baseUrl, communityService, auth);
        this.participantMinimum = 0;
    }

    public get enableRegistrationFlow$(): Observable<boolean> {
        const result$ = combineLatest([
            this.communityService.community$,
            this.communityService.currentAuctionOpenForNewRegistrations$,
        ]);

        return result$.pipe(
            map(([community, currentAuctionOpenForNewRegistrations]) => {
                return (
                    !community.preventInterestedIndividuals ||
                    currentAuctionOpenForNewRegistrations
                );
            }),
        );
    }

    inInterestedIndividualFlow(registration: Registration): boolean {
        return !registration.auction;
    }

    updateSurvey(id: string, update: Survey): Observable<void> {
        return this.http.put<void>(
            this.url + '/' + id + '/update-survey',
            update,
        );
    }

    allowedCancellationReasons(): Observable<string[]> {
        const url = `${this.url}/allowed-cancellation-reasons-for-consumer`;
        return this.http.get<string[]>(url);
    }

    allowedCancellationSubreasons(
        cancellationReason: string,
    ): Observable<string[]> {
        const url = `${this.url}/allowed-cancellation-subreasons`;
        return this.http.post<string[]>(url, { cancellationReason });
    }

    requestDeliveryCancellation(
        registrationId: string,
        cancelDelivery: CancelDelivery,
    ): Observable<void> {
        const url = `${this.url}/${registrationId}/delivery/request-cancellation-as-consumer`;
        return this.http.post<void>(url, cancelDelivery);
    }

    postConsumerCancellationExplanation(
        registrationId: string,
        explanation: string,
    ): Observable<void> {
        const url = `${this.url}/${registrationId}/delivery/consumer-cancellation-explanation`;
        return this.http.post<void>(url, {
            consumerCancellationExplanation: explanation,
        });
    }

    persistRegistration(input: {
        registration: Registration;
        communityCode: string;
    }): void {
        sessionStorage.setItem(
            `registration-${this.business}-${input.communityCode}`,
            JSON.stringify(input.registration),
        );
    }

    sendLinkToRegistration(registrationId: string): Observable<void> {
        const url = `${this.url}/${registrationId}/send-participant-link`;
        return this.http.post<void>(url, {});
    }

    add(registration: Registration): Observable<string> {
        return this.http
            .post<string>(this.url, registration, { observe: 'response' })
            .pipe(
                switchMap((response) => {
                    const registrationId = response.body;
                    return this.auth
                        .loginAsParticipant(response)
                        .pipe(switchMap((_) => of(registrationId)));
                }),
            );
    }

    get(id: string): Observable<Registration> {
        return this.http.get<Registration>(`${this.url}/${id}`).pipe(
            catchError((e) => {
                const unauthorizedStatuses = [401, 403, 404];
                if (
                    unauthorizedStatuses.find((status) => status === e?.status)
                ) {
                    return this.communityService.communityCode$.pipe(
                        take(1),
                        flatMap((communityCode) =>
                            from(
                                this.router.navigate([
                                    communityCode,
                                    'unauthorized',
                                    id,
                                ]),
                            ),
                        ),
                        map(() => new Registration()),
                    );
                }
                return throwError(e);
            }),
        );
    }
}
